/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useRef, useState } from 'react'
import { Tabs } from 'antd-mobile'
import useRootStore from '@store/useRootStore'

import ClubMessageFilterIco from '@assets/images/icon/etc_icon/club-message-filter.png'
// css
import { CenterTabWrap, Content, TabTxt, WhiteChildTabWrap } from '@styles/base.style'
import { Models } from 'rmc-tabs/lib/Models'
import { ListHeaderWrap } from '@views/Popup/Message/index.style'
import { Popover } from 'antd'
import { Button, QuickSpotsMenuBtnStyle } from '@styles/button.style'
import { SpotsQuickMenuList } from '@styles/modal'

import { MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY, MESSAGE_TYPE_TAB_INDEX_KEY } from '@constants/index'
import * as _ from 'lodash'
import { observer } from 'mobx-react'
import { CLUB_MEMBERSHIP_ID, MESSAGE_DIR } from 'adv-protocol/src/GameCommon'
import { useOnMount } from '@utils/reactExt'
import { isAccountForTesting, showToast, TOAST_TYPE } from '@utils/utils'
import { SimpleClubInfoItem } from '@store/clubStore'
import MessageList, { MessageListRef } from '../MessageList'

export interface MessageTabPros {
  getInitialClubId: () => string
}

const MessageTab: React.FC<MessageTabPros> = (props: MessageTabPros) => {

  const { clubStore, authStore, globalStore, messageStore, componentStore } = useRootStore()

  const { isSpecificClubMember } = authStore
  const {
    totalReceiveMessageCount, totalSendMessageCount, messageList, readableCount, receivedItemCount
  } = messageStore
  const { simpleClubs } = clubStore
  const { extraClubId } = componentStore

  const { getInitialClubId } = props

  const getInitializeMessageTypeTabKey = () => globalStore.clubMessageMode
    ? MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE : MESSAGE_TYPE_TAB_INDEX_KEY.MESSAGE
  const [currentMessageTypeTabKey, setCurrentMessageTypeTabKey] = useState(getInitializeMessageTypeTabKey())
  const [currentSendReceiveTabKey, setCurrentSendReceiveTabKey] = useState(MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE)
  const [clubsPopOverVisible, setClubsPopOverVisible] = useState(false)
  const [messagePopOverVisible, setMessagePopOverVisible] = useState(false)

  const messageSendReceiveTitleSuffix = currentMessageTypeTabKey === MESSAGE_TYPE_TAB_INDEX_KEY.MESSAGE ? '메시지' : '쪽지'
  const isTestAccount = isAccountForTesting(authStore.userInfo?.id || '')

  let filterClubs: SimpleClubInfoItem[] = []
  if (isSpecificClubMember) {
    filterClubs = _.filter(simpleClubs, club => club.isMember && club.grade !== CLUB_MEMBERSHIP_ID.FREE)
  } else {
    filterClubs = _.filter(simpleClubs, club => club.grade !== CLUB_MEMBERSHIP_ID.FREE)
  }

  const messageListRef = useRef({} as MessageListRef)

  const messageTypeTabs = [
    {
      title: <span>메시지</span>,
      key: MESSAGE_TYPE_TAB_INDEX_KEY.MESSAGE,
    },
    {
      title: <span>클럽쪽지</span>,
      key: MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE,
    },
  ]

  const messageSendReceiveTabs = [
    {
      title: <TabTxt className="flex-type">
        <span className="txt">받은 {messageSendReceiveTitleSuffix}</span>
        <span className="num">{totalReceiveMessageCount}</span>
      </TabTxt>,
      key: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE,
    },
    {
      title: <TabTxt className="flex-type">
        <span className="txt">보낸 {messageSendReceiveTitleSuffix}</span>
        <span className="num">{totalSendMessageCount}</span>
      </TabTxt>,
      key: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.SENT,
    },
  ]

  useOnMount(() => {
    if (authStore.isSignedIn) {
      clubStore.fetchSimpleClubs({})
    }
  })

  const onMessageTypeTabChange = (tab: Models.TabData) => {
    const key = tab.key as MESSAGE_TYPE_TAB_INDEX_KEY
    setCurrentMessageTypeTabKey(key)
    setCurrentSendReceiveTabKey(MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE)

    let clubId = null
    if (key === MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE) {
      clubId = getInitialClubId()
      globalStore.setClubMessageMode(true)
    } else {
      globalStore.setClubMessageMode(false)
    }
    componentStore.setExtraClubId(clubId)
  }

  const onSendReceiveTypeTabChange = (tab: Models.TabData) => {
    const key = tab.key as MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY
    setCurrentSendReceiveTabKey(key)

    if (currentMessageTypeTabKey !== MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE) {
      componentStore.setExtraClubId(null)
    }
  }

  const findMessageTypeTabIndexByTabKey = (key: MESSAGE_TYPE_TAB_INDEX_KEY) => {
    return _.findIndex(messageTypeTabs, tab => {
      return tab.key === key
    })
  }

  const onClickClub = (clubId: string) => {
    componentStore.setExtraClubId(clubId)
  }

  const onClubsPopOverVisibleChange = (visible: boolean) => {
    if (visible && _.size(filterClubs) < 1) {
      showToast(TOAST_TYPE.INFO, '가입한 클럽이 없습니다.')
      return
    }

    setClubsPopOverVisible(visible)
  }

  const onMessagePopOverVisibleChange = (visible: boolean) => {
    setMessagePopOverVisible(visible)
  }

  const onReceiveAllItems = () => {
    messageListRef.current.onReceiveAllItems()
    setMessagePopOverVisible(false)
  }

  const onClickReadAllMessage = () => {
    messageListRef.current.onClickReadAllMessage()
    setMessagePopOverVisible(false)
  }

  const onClickDeleteAllButton = () => {
    messageListRef.current.onClickDeleteAllButton()
    setMessagePopOverVisible(false)
  }

  const clubFilterList = (
    <SpotsQuickMenuList className="message-quick-list">
      <ul className="quick-menu-list">
        {filterClubs.map(club => (
          <li key={`MessageTab:clubFilterList:${club.id}`} className="quick-menu-list--item">
            <Button className="quick-btn" onClick={() => {
              setClubsPopOverVisible(false)
              onClickClub(club.id)
            }}>{club.name}</Button>
          </li>
        ))}
      </ul>
    </SpotsQuickMenuList>
  )

  const isVisibleReceiveAllButton = () => !extraClubId
    && currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE
  const isVisibleReadAllButton = () => currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE

  const messageQuickMenu = (
    <SpotsQuickMenuList className="message-quick-list">
      <ul className="quick-menu-list">
        {isVisibleReceiveAllButton() && (
          <li className="quick-menu-list--item">
            <Button
              className={`${receivedItemCount === 0 ? 'quick-btn disabled' : 'quick-btn'}`}
              onClick={onReceiveAllItems}
              disabled={receivedItemCount === 0}
            >
              모두 받기
            </Button>
          </li>
        )}
        {isVisibleReadAllButton() && (
          <li className="quick-menu-list--item">
            <Button
              className={`${readableCount === 0 ? 'quick-btn disabled' : 'quick-btn'}`}
              onClick={onClickReadAllMessage}
              disabled={readableCount === 0}
            >
              전체 읽기
            </Button>
          </li>
        )}
        <li className="quick-menu-list--item">
          <Button
            className={`quick-btn ${messageList?.total === 0 ? 'quick-btn disabled' : ''}`}
            onClick={onClickDeleteAllButton}
            disabled={messageList?.total === 0}
          >
            전체 삭제
          </Button>
        </li>
      </ul>
    </SpotsQuickMenuList>
  )

  return (
    <Fragment>
      <CenterTabWrap className="white-bg" style={{ marginBottom: '-1px', paddingTop: 18 }}>
        <Tabs
          tabs={messageTypeTabs}
          initialPage={findMessageTypeTabIndexByTabKey(currentMessageTypeTabKey)}
          page={findMessageTypeTabIndexByTabKey(currentMessageTypeTabKey)}
          onChange={onMessageTypeTabChange}
        />
      </CenterTabWrap>
      <WhiteChildTabWrap className="light">
        <Tabs
          tabs={messageSendReceiveTabs}
          initialPage={currentSendReceiveTabKey}
          page={currentSendReceiveTabKey}
          onChange={onSendReceiveTypeTabChange}
        />
        <ListHeaderWrap id="receivedMessageListHeader">

          {/* TODO 클럽쪽지 탭일 경우에만 나타나야 합니다. */}
          {currentMessageTypeTabKey === MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE &&
          !isTestAccount && (
            <Popover
              onVisibleChange={onClubsPopOverVisibleChange}
              visible={clubsPopOverVisible}
              placement="bottomRight"
              content={clubFilterList}
              trigger="click"
              overlayClassName="betting-history-quick-menu"
              getPopupContainer={() => document.getElementById('receivedMessageListHeader')}
            >
              <Button className="club-message-filter-btn" css={QuickSpotsMenuBtnStyle}>
                <img src={ClubMessageFilterIco} alt="클럽메시지 필터"/>
              </Button>
            </Popover>
          )}

          <Popover
            onVisibleChange={onMessagePopOverVisibleChange}
            visible={messagePopOverVisible}
            placement="bottomRight"
            content={messageQuickMenu}
            trigger="click"
            overlayClassName="betting-history-quick-menu"
            getPopupContainer={() => document.getElementById('receivedMessageListHeader')}
          >
            <Button className="message-setting-btn" css={QuickSpotsMenuBtnStyle}>
              <span className="spots"/>
            </Button>
          </Popover>
        </ListHeaderWrap>
      </WhiteChildTabWrap>


      <Content
        key={currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE ? 'receive' : 'send'}
        style={{ height: '100%', background: '#eee' }}>
        <MessageList
          currentMessageTypeTabKey={currentMessageTypeTabKey}
          currentSendReceiveTabKey={currentSendReceiveTabKey}
          dir={currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE
            ? MESSAGE_DIR.RECV : MESSAGE_DIR.SEND}
          clubId={extraClubId}
          messageListRef={messageListRef}
        />
      </Content>
    </Fragment>
  )
}

export default observer(MessageTab)
